import React, { useState } from 'react';
import { Plus, Calendar, Filter, ChevronDown, Search, AlertTriangle } from 'lucide-react';
import appointmentService from '../services/appointmentService';
import jobService from '../services/jobService';
import { getFranchiseeIdForCRUD, getFranchiseeIdForDataLoading } from '../App/services/appHelpers';
import { formatDuration } from '../utils/formatDuration';
import useMobileHeaderActions from '../hooks/useMobileHeaderActions';
import useTechnicianFilter from '../hooks/useTechnicianFilter';
import TechnicianFilter from './TechnicianFilter';
import MobileFAB from './MobileFAB';
import StatusSelect, { JOB_STATUS_OPTIONS } from './StatusSelect';

const JobList = ({ jobs, customers, onJobSelect, onAddJob, onAddContract, user, userProfile, onRefreshData, initialFilter }) => {
  const isAdmin = (userProfile || user)?.role === 'admin';
  const techFilter = useTechnicianFilter(userProfile);
  const [statusFilter, setStatusFilter] = useState(initialFilter || 'active');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [datePreset, setDatePreset] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedJobAppointments, setExpandedJobAppointments] = useState({});
  const [jobAppointments, setJobAppointments] = useState({});

  useMobileHeaderActions(
    techFilter.showFilter ? <TechnicianFilter {...techFilter} compact /> : null,
    [techFilter.showFilter, techFilter.selectedTechnician, techFilter.techGroups]
  );

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : 'Unknown Customer';
  };

  const getCustomer = (customerId) => {
    return customers.find(c => c.id === customerId);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-blue-100 text-blue-800';
      case 'on_hold':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Compute whether a job is "unscheduled" (active with no future appointments)
  const isUnscheduled = (job) => {
    return job.status === 'active' && (!job.next_appointment_date || new Date(job.next_appointment_date) < new Date());
  };

  // Scope jobs by technician filter
  const filteredFranchiseeId = techFilter.showFilter ? techFilter.getFilteredFranchiseeId() : null;
  const selectedBranchId = techFilter.getSelectedBranchId();
  const scopedJobs = techFilter.selectedTechnician === 'all' ? jobs : jobs.filter(job => {
    if (filteredFranchiseeId && filteredFranchiseeId !== 'all' && job.franchisee_id !== filteredFranchiseeId) return false;
    if (selectedBranchId && job.branch_id !== selectedBranchId) return false;
    return true;
  });

  // Compute counts for status filter pills
  const statusCounts = {
    all: scopedJobs.length,
    active: scopedJobs.filter(j => j.status === 'active').length,
    unscheduled_jobs: scopedJobs.filter(j => isUnscheduled(j) && !j.is_contract).length,
    unscheduled_contracts: scopedJobs.filter(j => isUnscheduled(j) && j.is_contract).length,
    on_hold: scopedJobs.filter(j => j.status === 'on_hold').length,
    completed: scopedJobs.filter(j => j.status === 'completed').length,
    cancelled: scopedJobs.filter(j => j.status === 'cancelled').length,
  };

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'unscheduled_jobs', label: 'Unscheduled Jobs', icon: true },
    { value: 'unscheduled_contracts', label: 'Unscheduled Contracts', icon: true },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const datePresetOptions = [
    { value: 'all', label: 'All Dates' },
    { value: 'today', label: 'Today' },
    { value: '7', label: 'Last 7 Days' },
    { value: '30', label: 'Last 30 Days' },
    { value: '90', label: 'Last 90 Days' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'this_year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const getDateRange = () => {
    if (datePreset === 'custom') return { from: dateFrom, to: dateTo };
    if (datePreset === 'all') return { from: '', to: '' };

    const today = new Date();
    const to = today.toISOString().slice(0, 10);

    if (datePreset === 'today') {
      return { from: to, to };
    }
    if (datePreset === 'this_month') {
      const from = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
      return { from, to };
    }
    if (datePreset === 'last_month') {
      const from = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().slice(0, 10);
      const lastDay = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().slice(0, 10);
      return { from, to: lastDay };
    }
    if (datePreset === 'this_year') {
      const from = new Date(today.getFullYear(), 0, 1).toISOString().slice(0, 10);
      return { from, to };
    }

    // Numeric presets (7, 30, 90 days)
    const days = parseInt(datePreset, 10);
    const from = new Date(today.getTime() - days * 86400000).toISOString().slice(0, 10);
    return { from, to };
  };

  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset && preset !== 'all' && preset !== 'custom') {
      // Populate date fields from preset so they're visible and editable
      const range = getDateRange.call ? null : null; // can't call getDateRange yet as preset state hasn't updated
      // Compute range inline using the same logic
      const today = new Date();
      const todayStr = today.toISOString().slice(0, 10);
      let from = '', to = '';
      if (preset === 'today') {
        from = todayStr; to = todayStr;
      } else if (preset === 'this_month') {
        from = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
        to = todayStr;
      } else if (preset === 'last_month') {
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().slice(0, 10);
        to = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().slice(0, 10);
      } else if (preset === 'this_year') {
        from = new Date(today.getFullYear(), 0, 1).toISOString().slice(0, 10);
        to = todayStr;
      } else {
        const days = parseInt(preset, 10);
        from = new Date(today.getTime() - days * 86400000).toISOString().slice(0, 10);
        to = todayStr;
      }
      setDateFrom(from);
      setDateTo(to);
    } else if (preset === 'all') {
      setDateFrom('');
      setDateTo('');
    }
  };

  const handleDateManualChange = (setter) => (value) => {
    setter(value);
    setDatePreset('custom');
  };

  const clearFilters = () => {
    setStatusFilter('active');
    setDatePreset('all');
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
  };

  const hasActiveFilters = datePreset !== 'all' || searchTerm;

  const filteredJobs = scopedJobs.filter(job => {
    // Status filter
    if (statusFilter === 'unscheduled_jobs') {
      if (!isUnscheduled(job) || job.is_contract) return false;
    } else if (statusFilter === 'unscheduled_contracts') {
      if (!isUnscheduled(job) || !job.is_contract) return false;
    } else if (statusFilter !== 'all' && job.status !== statusFilter) return false;

    // Date filters (using next_appointment_date or created_at as fallback)
    const { from: effectiveDateFrom, to: effectiveDateTo } = getDateRange();
    if (effectiveDateFrom || effectiveDateTo) {
      const jobDate = job.next_appointment_date || job.created_at;
      if (jobDate) {
        const d = new Date(jobDate).toISOString().slice(0, 10);
        if (effectiveDateFrom && d < effectiveDateFrom) return false;
        if (effectiveDateTo && d > effectiveDateTo) return false;
      }
    }

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      const customerName = (job.customer_name || getCustomerName(job.customer_id)).toLowerCase();
      const searchFields = [
        job.category_name,
        customerName,
        job.description,
        job.treatment_address_line1,
        job.treatment_address_city,
        job.treatment_address_postcode,
        job.treatment_contact_name,
        job.treatment_contact_company,
        job.franchisee_company
      ];
      const matches = searchFields.some(field => field && String(field).toLowerCase().includes(term));
      if (!matches) return false;
    }

    return true;
  });

  // Helper function to format treatment address from job
  const formatTreatmentAddress = (job) => {
    const parts = [];

    if (job.treatment_address_line1) parts.push(job.treatment_address_line1);
    if (job.treatment_address_city) parts.push(job.treatment_address_city);
    if (job.treatment_address_postcode) parts.push(job.treatment_address_postcode);

    return parts.length > 0 ? parts.join(', ') : '';
  };

  // Helper function to get treatment contact info if different from customer
  const getTreatmentContactInfo = (job) => {
    const parts = [];

    // Check if treatment contact name is different from customer name
    // Use customer_name from the job data (from backend JOIN)
    const customerName = job.customer_name || getCustomerName(job.customer_id);
    if (job.treatment_contact_name &&
        job.treatment_contact_name.trim() !== '' &&
        job.treatment_contact_name !== customerName) {
      parts.push(job.treatment_contact_name);
    }

    // Check if treatment contact company is different from customer company
    // Try to get from customers array, but don't fail if not found
    const customer = getCustomer(job.customer_id);
    if (job.treatment_contact_company &&
        job.treatment_contact_company.trim() !== '' &&
        job.treatment_contact_company !== customer?.company_name) {
      parts.push(job.treatment_contact_company);
    }

    return parts.length > 0 ? parts.join(' - ') : '';
  };

  const toggleJobAppointments = async (jobId, event) => {
    // Prevent job click event from triggering
    event.stopPropagation();

    const isExpanded = expandedJobAppointments[jobId];

    if (isExpanded) {
      // Collapse
      setExpandedJobAppointments(prev => ({ ...prev, [jobId]: false }));
    } else {
      // Expand and load appointments if not already loaded
      if (!jobAppointments[jobId] && user) {
        try {
          const franchiseeId = getFranchiseeIdForDataLoading(user);
          const response = await appointmentService.getAppointments(franchiseeId, { job_id: jobId });
          const appointments = response.appointments || [];

          // Separate past and future appointments
          const now = new Date();
          const pastAppointments = appointments.filter(apt => new Date(apt.scheduled_date) < now).sort((a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date));
          const futureAppointments = appointments.filter(apt => new Date(apt.scheduled_date) >= now).sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));

          setJobAppointments(prev => ({
            ...prev,
            [jobId]: { past: pastAppointments, future: futureAppointments }
          }));
        } catch (error) {
          console.error('Error loading appointments:', error);
        }
      }
      setExpandedJobAppointments(prev => ({ ...prev, [jobId]: true }));
    }
  };

  const handleJobStatusChange = async (job, newStatus) => {
    try {
      const franchiseeId = getFranchiseeIdForCRUD(user, job.franchisee_id);
      await jobService.updateJob(franchiseeId, job.id, { status: newStatus });
      if (onRefreshData) onRefreshData();
    } catch (error) {
      console.error('Error updating job status:', error);
    }
  };

  return (
    <div className="px-2 py-2 md:p-6">
      <MobileFAB onAddJob={onAddJob} onAddContract={onAddContract} />
      {/* Desktop Layout */}
      <div className="hidden md:flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Jobs</h1>
          <p className="text-gray-600">Manage all pest control jobs</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onAddJob}
            className="btn btn-md btn-secondary"
          >
            <Plus size={16} />
            <span>Add Job</span>
          </button>
          <button
            onClick={onAddContract}
            className="btn btn-md btn-secondary"
          >
            <Plus size={16} />
            <span>Add Contract</span>
          </button>
          <TechnicianFilter {...techFilter} />
        </div>
      </div>

      {/* Mobile subtitle */}
      <div className="md:hidden mb-2">
        <p className="text-gray-600 text-xs">Manage all pest control jobs</p>
      </div>

      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 border-b border-gray-200 space-y-3">
          {/* Search + Filters button row */}
          <div className="flex gap-3">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-transparent text-sm"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn btn-md btn-outline ${hasActiveFilters ? '!border-pest-blue !text-pest-blue' : ''}`}
            >
              <Filter size={16} />
              <span>Filters</span>
              {hasActiveFilters && <span className="bg-pest-blue text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">!</span>}
            </button>
          </div>

          {/* Status filter pills */}
          <div className="flex flex-wrap gap-2">
            {statusOptions.map(option => (
              <button
                key={option.value}
                onClick={() => setStatusFilter(option.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors flex items-center gap-1 ${
                  statusFilter === option.value
                    ? option.icon
                      ? 'bg-amber-500 text-white border-amber-500'
                      : 'bg-pest-blue text-white border-pest-blue'
                    : option.icon && statusCounts[option.value] > 0
                      ? 'bg-white text-amber-600 border-amber-300 hover:border-amber-400 hover:text-amber-700'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400 hover:text-gray-800'
                }`}
              >
                {option.icon && <AlertTriangle size={14} />}
                {option.label} ({statusCounts[option.value] || 0})
              </button>
            ))}
          </div>

          {showFilters && (
            <div className="pt-3 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <select
                  value={datePreset}
                  onChange={(e) => handleDatePresetChange(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-pest-blue focus:border-transparent"
                >
                  {datePresetOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {datePreset !== 'all' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">From</label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => handleDateManualChange(setDateFrom)(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-pest-blue focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">To</label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => handleDateManualChange(setDateTo)(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-pest-blue focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Clear all filters
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="p-4">
          {filteredJobs.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">
                {searchTerm ? 'No jobs match your search.' : statusFilter === 'unscheduled' ? 'No unscheduled jobs found.' : `No ${statusFilter} jobs found.`}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredJobs.map((job) => (
              <div
                key={job.id}
                onClick={() => onJobSelect(job)}
                className="p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
                      <h4 className="font-medium text-gray-900">
                        {job.category_name || 'Job'}
                      </h4>
                      <StatusSelect
                        value={job.status || 'active'}
                        onChange={(val) => handleJobStatusChange(job, val)}
                        options={JOB_STATUS_OPTIONS}
                        size="sm"
                      />
                    </div>
                    <p className="text-sm font-medium text-gray-800 mt-1">
                      {job.customer_name || getCustomerName(job.customer_id)}
                    </p>
                    {getTreatmentContactInfo(job) && (
                      <p className="text-sm text-blue-600 font-medium mt-1">
                        {getTreatmentContactInfo(job)}
                      </p>
                    )}
                    <p className="text-sm text-gray-600 mt-1">
                      {formatTreatmentAddress(job) || 'No address'}
                    </p>
                    {job.description && (
                      <p className="text-xs text-gray-500 mt-1">
                        {job.description}
                      </p>
                    )}
                    {(() => {
                      const hasNextFuture = job.next_appointment_date && new Date(job.next_appointment_date) >= new Date();
                      const pastCount = job.past_appointment_count || 0;
                      const futureExtra = hasNextFuture
                        ? job.total_appointments - pastCount - 1
                        : job.total_appointments - pastCount;
                      const formatType = (t) => t ? t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null;

                      return (
                        <>
                          {/* Next appointment line */}
                          <div className="flex items-center text-sm text-gray-600 mt-2 flex-wrap gap-1">
                            <Calendar size={14} className="flex-shrink-0" />
                            {hasNextFuture ? (
                              <>
                                <span>
                                  Next: {new Date(job.next_appointment_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </span>
                                <span className="mx-1">&middot;</span>
                                <span>
                                  {new Date(job.next_appointment_date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                {job.next_appointment_duration && (
                                  <>
                                    <span className="mx-1">&middot;</span>
                                    <span>{formatDuration(job.next_appointment_duration)}</span>
                                  </>
                                )}
                                {job.next_appointment_type && (
                                  <>
                                    <span className="mx-1">&middot;</span>
                                    <span className="text-indigo-600">{formatType(job.next_appointment_type)}</span>
                                  </>
                                )}
                              </>
                            ) : (
                              <span className="text-gray-400">Next: No scheduled appointments</span>
                            )}
                          </div>

                          {/* Next appointment notes */}
                          {hasNextFuture && job.next_appointment_notes && (
                            <p className="text-xs text-gray-500 italic mt-1 ml-5">{job.next_appointment_notes}</p>
                          )}

                          {/* Previous / more upcoming link */}
                          {(pastCount > 0 || futureExtra > 0) && (
                            <div className="mt-1 ml-5">
                              <button
                                onClick={(e) => toggleJobAppointments(job.id, e)}
                                className="flex items-center gap-1 text-sm text-pest-blue hover:text-pest-teal transition-colors"
                              >
                                <span>
                                  {(() => {
                                    const parts = [];
                                    if (pastCount > 0) parts.push(`${pastCount} previous`);
                                    if (futureExtra > 0) parts.push(`${futureExtra} more upcoming`);
                                    return parts.join(', ');
                                  })()}
                                </span>
                                <ChevronDown
                                  size={14}
                                  className={`transition-transform flex-shrink-0 ${expandedJobAppointments[job.id] ? 'rotate-180' : ''}`}
                                />
                              </button>
                            </div>
                          )}

                          {/* Expanded Appointments List */}
                          {expandedJobAppointments[job.id] && jobAppointments[job.id] && (
                            <div className="mt-2 ml-5 space-y-2 border-l-2 border-gray-200 pl-3">
                              {jobAppointments[job.id].future.filter((_, i) => !hasNextFuture || i > 0).length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-700 mb-1">Upcoming:</p>
                                  {jobAppointments[job.id].future.filter((_, i) => !hasNextFuture || i > 0).map(apt => (
                                    <div key={apt.id} className="py-1">
                                      <div className="text-xs text-gray-600 flex items-center gap-1 flex-wrap">
                                        <span className="font-medium">
                                          {new Date(apt.scheduled_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        </span>
                                        <span>&middot;</span>
                                        <span>
                                          {new Date(apt.scheduled_date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                        {apt.estimated_duration && (
                                          <>
                                            <span>&middot;</span>
                                            <span>{formatDuration(apt.estimated_duration)}</span>
                                          </>
                                        )}
                                        {apt.appointment_type && (
                                          <>
                                            <span>&middot;</span>
                                            <span className="text-indigo-600">{formatType(apt.appointment_type)}</span>
                                          </>
                                        )}
                                      </div>
                                      {apt.notes && (
                                        <p className="text-xs text-gray-400 italic mt-0.5">{apt.notes}</p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {jobAppointments[job.id].past.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-700 mb-1">Past:</p>
                                  {jobAppointments[job.id].past.map(apt => (
                                    <div key={apt.id} className="py-1">
                                      <div className="text-xs text-gray-500 flex items-center gap-1 flex-wrap">
                                        <span className="font-medium">
                                          {new Date(apt.scheduled_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        </span>
                                        <span>&middot;</span>
                                        <span>
                                          {new Date(apt.scheduled_date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                        {apt.estimated_duration && (
                                          <>
                                            <span>&middot;</span>
                                            <span>{formatDuration(apt.estimated_duration)}</span>
                                          </>
                                        )}
                                        {apt.appointment_type && (
                                          <>
                                            <span>&middot;</span>
                                            <span className="text-indigo-600">{formatType(apt.appointment_type)}</span>
                                          </>
                                        )}
                                      </div>
                                      {apt.notes && (
                                        <p className="text-xs text-gray-400 italic mt-0.5">{apt.notes}</p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <div className="text-right flex-shrink-0">
                    {job.price && (
                      <div className="text-lg font-semibold text-gray-900 mb-2">
                        £{parseFloat(job.price).toFixed(2)}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 space-y-0.5">
                      <div>Created: {new Date(job.created_at).toLocaleDateString()}</div>
                      <div>Updated: {new Date(job.updated_at || job.created_at).toLocaleDateString()}</div>
                      {isAdmin && job.franchisee_company && (
                        <div className="text-purple-600 font-medium">{job.franchisee_company}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobList;